import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Bot, User } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm the Institute of Analytics USA\u2122 advisor. Ask me anything about our PG Diploma in AI, ML & Data Science Engineering \u2014 courses, pricing, career outcomes, or enrollment!" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { message: msg, session_id: sessionId }, { withCredentials: true });
      setSessionId(data.session_id);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I'm having trouble connecting. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          data-testid="chatbot-toggle"
          className="fixed bottom-20 right-6 z-[9999] w-14 h-14 bg-[#0077B6] text-white flex items-center justify-center shadow-lg hover:-translate-y-1 hover:shadow-[4px_4px_0px_#09090B] transition-all duration-150"
          style={{ pointerEvents: 'auto' }}
        >
          <MessageCircle className="w-6 h-6" strokeWidth={1.5} />
        </button>
      )}

      {open && (
        <div data-testid="chatbot-panel" className="fixed bottom-20 right-6 z-[9999] w-96 max-w-[calc(100vw-2rem)] bg-[#111111] border border-[#262626] shadow-2xl flex flex-col" style={{ height: '480px', pointerEvents: 'auto' }}>
          <div className="flex items-center justify-between px-4 py-3 bg-[#0077B6] text-white">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium text-sm" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>IoA USA Advisor</span>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chatbot-close" className="hover:opacity-70">
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 bg-[#0077B6]/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <Bot className="w-4 h-4 text-[#0077B6]" strokeWidth={1.5} />
                  </div>
                )}
                <div className={`max-w-[80%] px-3 py-2 text-base leading-relaxed ${
                  m.role === 'user' 
                    ? 'bg-[#0077B6] text-white' 
                    : 'bg-[#1a1a1a] text-zinc-200'
                }`}>
                  {m.content}
                </div>
                {m.role === 'user' && (
                  <div className="w-7 h-7 bg-zinc-200 flex items-center justify-center flex-shrink-0 mt-1">
                    <User className="w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                  </div>
                )}
              </div>
            ))}
            {loading && (
              <div className="flex gap-2 items-start">
                <div className="w-7 h-7 bg-[#0077B6]/10 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-[#0077B6]" strokeWidth={1.5} />
                </div>
                <div className="bg-[#1a1a1a] px-3 py-2 text-sm text-zinc-300">Thinking...</div>
              </div>
            )}
          </div>

          <div className="border-t border-[#262626] p-3 flex gap-2">
            <input
              data-testid="chatbot-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder="Ask about courses, pricing, careers..."
              className="flex-1 px-3 py-2 text-base bg-[#0A0A0A] text-white placeholder-zinc-500 border border-[#262626] focus:border-[#0077B6] focus:ring-1 focus:ring-[#0077B6] outline-none"
            />
            <button
              onClick={send}
              disabled={loading || !input.trim()}
              data-testid="chatbot-send"
              className="brand-btn px-3 py-2 disabled:opacity-50 disabled:transform-none disabled:shadow-none"
            >
              <Send className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
